// Shared consent state for anything non-essential (currently just Clarity).
// Plain localStorage + a couple of window events rather than a context
// provider, since the only two consumers -- the banner and
// ClarityAnalytics -- don't share a render tree and don't need re-renders,
// just a "has this changed" signal.
export type ConsentValue = 'accepted' | 'declined'

const KEY = 'flowscale-cookie-consent'
const CHANGE_EVENT = 'flowscale-cookie-consent-change'
const REOPEN_EVENT = 'flowscale-cookie-consent-reopen'

export function getConsent(): ConsentValue | null {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'accepted' || v === 'declined' ? v : null
  } catch {
    // Private browsing / blocked storage: treat as undecided every visit,
    // which just means the banner reappears -- never means tracking runs
    // without being asked.
    return null
  }
}

export function setConsent(value: ConsentValue) {
  try {
    localStorage.setItem(KEY, value)
  } catch {
    // Storage blocked -- the choice still applies for this page load via
    // the event below, it just won't be remembered next visit.
  }
  window.dispatchEvent(new CustomEvent<ConsentValue>(CHANGE_EVENT, { detail: value }))
}

export function onConsentChange(cb: (value: ConsentValue) => void) {
  const handler = (e: Event) => cb((e as CustomEvent<ConsentValue>).detail)
  window.addEventListener(CHANGE_EVENT, handler)
  return () => window.removeEventListener(CHANGE_EVENT, handler)
}

export function reopenConsentBanner() {
  window.dispatchEvent(new Event(REOPEN_EVENT))
}

export function onReopenRequest(cb: () => void) {
  window.addEventListener(REOPEN_EVENT, cb)
  return () => window.removeEventListener(REOPEN_EVENT, cb)
}
